import { normalizeUrl } from 'fym-common/src/utils/normalizeUrl';
import { lyricLinesOf } from 'fym-shared/src/music24/lyricTiming';
import { CatalogItemKind, CatalogItemOwnership } from 'fym-shared/src/smartlinks/catalog';
import { CatalogItem, CatalogSong } from '../../../../../../redux/api/fymAPI.types';
import { normalizeIsrc } from '../../normalizeIsrc';

/** The longest title and artist name the server takes (music24-api `titleSchema`). */
export const SONG_TITLE_MAX_LENGTH = 500;

/** The longest lyrics the server takes (music24-api `PATCH .../song`). */
export const SONG_LYRICS_MAX_LENGTH = 6000;

/** The cover art of an unreleased row: the uploaded file. `url` is the full address for the preview. */
export interface SongDraftArtwork {
  id: number;
  url: string;
}

/**
 * The text fields of the song page that the save bar collects (design song-page b3, d2). The
 * lyric times, the master and the service links are not here: they save at once (owner decision 1;
 * the links: "Where it plays", SoundCloud connector design d, 2026-10-15).
 */
export interface SongDraft {
  title: string;
  artistName: string;
  /** '' = no type. */
  releaseType: string;
  /** `YYYY-MM-DD`, '' = no date. */
  releaseDate: string;
  artwork: SongDraftArtwork | null;
  isrc: string;
  lyrics: string;
  ownership: CatalogItemOwnership;
}

export type SongDraftField = keyof SongDraft;

/** The order of the fields in the save bar and in the leave guard. */
export const SONG_DRAFT_FIELDS: SongDraftField[] = [
  'title',
  'artistName',
  'releaseType',
  'releaseDate',
  'artwork',
  'isrc',
  'lyrics',
  'ownership',
];

/** The fields that `PATCH .../catalog/:uuid` takes; the others go to `PATCH .../song`. */
const ITEM_FIELDS: ReadonlySet<SongDraftField> = new Set<SongDraftField>([
  'title',
  'artistName',
  'releaseType',
  'releaseDate',
  'artwork',
  'ownership',
]);

/**
 * The fields that only an unreleased row can change (a claimed row keeps the facts of the
 * services). The cover is not one of them: a claimed row can change its cover in Music24 (cover
 * editor D8; the API takes `artworkFileId` on a claimed row).
 */
const UNRELEASED_ONLY: ReadonlySet<SongDraftField> = new Set<SongDraftField>([
  'title',
  'artistName',
  'releaseType',
  'releaseDate',
  'isrc',
]);

/** The fields that only a claimed row can change. */
const CLAIMED_ONLY: ReadonlySet<SongDraftField> = new Set<SongDraftField>(['ownership']);

/** `YYYY-MM-DD` of an ISO date, or ''. The server stores a day; the time part is not a choice. */
export function dayOf(iso: string | null): string {
  return iso === null ? '' : iso.slice(0, 10);
}

/** The draft that equals what the server holds now. */
export function draftFromServer(
  item: CatalogItem,
  song: CatalogSong | undefined,
  artworkUrl: (relative: string) => string
): SongDraft {
  return {
    title: item.title,
    artistName: item.artistName,
    releaseType: item.releaseType ?? '',
    releaseDate: dayOf(item.releaseDate),
    artwork: item.artworkFile === null ? null : { id: item.artworkFile.id, url: artworkUrl(item.artworkFile.url) },
    isrc: song?.isrc ?? '',
    lyrics: song?.lyrics ?? '',
    ownership: item.ownership,
  };
}

/** The links as the server stores them: no empty field, each address normalized. Null when none. */
export function cleanServiceLinks(links: Record<string, string>): Record<string, string> | null {
  const cleaned: Record<string, string> = {};
  Object.entries(links).forEach(([integrationType, url]) => {
    const trimmed = url.trim();
    if (trimmed !== '') {
      cleaned[integrationType] = normalizeUrl(trimmed);
    }
  });
  return Object.keys(cleaned).length === 0 ? null : cleaned;
}

function sameValue(field: SongDraftField, base: SongDraft, draft: SongDraft): boolean {
  switch (field) {
    case 'title':
    case 'artistName':
    case 'releaseType':
    case 'lyrics':
      return base[field].trim() === draft[field].trim();
    case 'isrc':
      return (normalizeIsrc(base.isrc) ?? base.isrc.trim()) === (normalizeIsrc(draft.isrc) ?? draft.isrc.trim());
    case 'artwork':
      return (base.artwork?.id ?? null) === (draft.artwork?.id ?? null);
    case 'releaseDate':
    case 'ownership':
      return base[field] === draft[field];
  }
}

/** True when the row kind lets the user change this field. */
export function isEditableField(field: SongDraftField, kind: CatalogItemKind): boolean {
  if (kind === CatalogItemKind.unreleased) {
    return !CLAIMED_ONLY.has(field);
  }
  return !UNRELEASED_ONLY.has(field);
}

/** The fields that differ from the server, in the order of the page. */
export function changedFields(base: SongDraft, draft: SongDraft, kind: CatalogItemKind): SongDraftField[] {
  return SONG_DRAFT_FIELDS.filter((field) => isEditableField(field, kind) && !sameValue(field, base, draft));
}

/** Why a field cannot save. The keys are the fields; a value names the rule it breaks. */
export type SongDraftErrors = Partial<Record<SongDraftField, 'required' | 'tooLong' | 'isrc'>>;

/** The checks of the client. The server checks again; a 400 shows under the field too. */
export function validateDraft(draft: SongDraft, kind: CatalogItemKind): SongDraftErrors {
  const errors: SongDraftErrors = {};
  if (kind === CatalogItemKind.unreleased) {
    if (draft.title.trim() === '') {
      errors.title = 'required';
    } else if (draft.title.trim().length > SONG_TITLE_MAX_LENGTH) {
      errors.title = 'tooLong';
    }
    if (draft.artistName.trim() === '') {
      errors.artistName = 'required';
    } else if (draft.artistName.trim().length > SONG_TITLE_MAX_LENGTH) {
      errors.artistName = 'tooLong';
    }
    if (draft.isrc.trim() !== '' && normalizeIsrc(draft.isrc) === null) {
      errors.isrc = 'isrc';
    }
  }
  if (draft.lyrics.length > SONG_LYRICS_MAX_LENGTH) {
    errors.lyrics = 'tooLong';
  }
  return errors;
}

export interface SongDraftPatches {
  /** The body of `PATCH .../catalog/:uuid`, or null when no item field changed. */
  item: {
    title?: string;
    artistName?: string;
    releaseType?: string | null;
    releaseDate?: string | null;
    artworkFileId?: number | null;
    ownership?: CatalogItemOwnership;
  } | null;
  /** The body of `PATCH .../catalog/:uuid/song`, or null when no song field changed. */
  song: { lyrics?: string | null; isrc?: string | null } | null;
}

/** The two bodies of one Save: only the parts that changed (design b3 note). */
export function draftPatches(fields: SongDraftField[], draft: SongDraft): SongDraftPatches {
  const item: NonNullable<SongDraftPatches['item']> = {};
  const song: NonNullable<SongDraftPatches['song']> = {};
  fields.forEach((field) => {
    switch (field) {
      case 'title':
        item.title = draft.title.trim();
        break;
      case 'artistName':
        item.artistName = draft.artistName.trim();
        break;
      case 'releaseType':
        item.releaseType = draft.releaseType.trim() === '' ? null : draft.releaseType.trim();
        break;
      case 'releaseDate':
        item.releaseDate = draft.releaseDate === '' ? null : draft.releaseDate;
        break;
      case 'artwork':
        item.artworkFileId = draft.artwork?.id ?? null;
        break;
      case 'ownership':
        item.ownership = draft.ownership;
        break;
      case 'isrc':
        song.isrc = draft.isrc.trim() === '' ? null : (normalizeIsrc(draft.isrc) ?? draft.isrc.trim());
        break;
      case 'lyrics':
        song.lyrics = draft.lyrics.trim() === '' ? null : draft.lyrics.trim();
        break;
      default:
        break;
    }
  });
  return {
    item: fields.some((field) => ITEM_FIELDS.has(field)) ? item : null,
    song: fields.some((field) => !ITEM_FIELDS.has(field)) ? song : null,
  };
}

/** True when the field goes to `PATCH .../catalog/:uuid`. */
export function isItemField(field: SongDraftField): boolean {
  return ITEM_FIELDS.has(field);
}

/**
 * The lyric lines of the new text that the server syncs again: a line whose text is not a line of
 * the old text. The server keeps the times of the other lines (spec §5.6). Same split as the
 * server (`lyricLinesOf`), so the count is the server count.
 */
export function changedLyricLineCount(oldLyrics: string, newLyrics: string): number {
  const left = new Map<string, number>();
  lyricLinesOf(oldLyrics).forEach(({ text }) => left.set(text, (left.get(text) ?? 0) + 1));
  let changed = 0;
  lyricLinesOf(newLyrics).forEach(({ text }) => {
    const count = left.get(text) ?? 0;
    if (count > 0) {
      left.set(text, count - 1);
    } else {
      changed += 1;
    }
  });
  return changed;
}
